import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TemplateLibrary = () => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const categories = [
    { value: '', label: 'All Categories' },
    { value: 'cold-outreach', label: 'Cold Outreach' },
    { value: 'follow-up', label: 'Follow-up' },
    { value: 'demo-invitation', label: 'Demo Invitation' },
    { value: 'thank-you', label: 'Thank You' },
    { value: 'nurturing', label: 'Lead Nurturing' }
  ];

  const templates = [
    {
      id: 1,
      name: 'Cold Email Introduction',
      category: 'cold-outreach',
      channel: 'email',
      subject: 'Quick question about {{company_name}}\'s growth goals',
      content: `Hi {{first_name}},\n\nI noticed {{company_name}} has been expanding rapidly in the {{industry}} space. Congratulations on your recent growth!\n\nI'm reaching out because we've helped similar companies like yours increase their lead generation by 300% using AI-powered targeting.\n\nWould you be open to a brief 15-minute call to discuss how this could benefit {{company_name}}?\n\nBest regards,\n{{sender_name}}`,
      usage: 245,
      performance: {
        openRate: 28.5,
        replyRate: 4.2,
        conversionRate: 1.8
      },
      lastUsed: '2025-01-08'
    },
    {
      id: 2,
      name: 'LinkedIn Connection Request',
      category: 'cold-outreach',
      channel: 'social',
      subject: 'Connection Request',
      content: `Hi {{first_name}},\n\nI came across your profile and was impressed by your work at {{company_name}}. I'd love to connect and share some insights about {{industry}} trends.\n\nLooking forward to connecting!`,
      usage: 189,
      performance: {
        acceptanceRate: 45.2,
        responseRate: 12.8,
        conversionRate: 3.1
      },
      lastUsed: '2025-01-07'
    },
    {
      id: 3,
      name: 'Follow-up After Demo',category: 'follow-up',channel: 'email',subject: 'Thanks for your time today, {{first_name}}',
      content: `Hi {{first_name}},\n\nThank you for taking the time to see our demo today. I hope you found it valuable and can see how our solution could help {{company_name}} achieve its goals.\n\nAs discussed, I'm attaching the case study we mentioned and the pricing information.\n\nI'd love to schedule a follow-up call to answer any questions you might have. Are you available next week?\n\nBest regards,\n{{sender_name}}`,
      usage: 156,
      performance: {
        openRate: 65.4,
        replyRate: 18.7,
        conversionRate: 12.3
      },
      lastUsed: '2025-01-08'
    },
    {
      id: 4,
      name: 'SMS Follow-up',category: 'follow-up',channel: 'sms',subject: 'SMS Follow-up',
      content: `Hi {{first_name}}, following up on our conversation about {{topic}}. Would you like to schedule a quick call this week? - {{sender_name}}`,
      usage: 98,
      performance: {
        deliveryRate: 98.2,
        replyRate: 8.5,
        conversionRate: 2.1
      },
      lastUsed: '2025-01-06'
    },
    {
      id: 5,
      name: 'Demo Invitation Email',category: 'demo-invitation',channel: 'email',subject: 'See how {{company_name}} can increase leads by 300%',
      content: `Hi {{first_name}},\n\nBased on our previous conversation, I think you'd be interested in seeing exactly how companies like {{company_name}} are using our platform to:\n\n• Generate 3x more qualified leads\n• Reduce cost per acquisition by 40%\n• Automate 80% of their outreach process\n\nWould you be available for a 20-minute demo this week? I can show you real results from companies in the {{industry}} industry.\n\nBest regards,\n{{sender_name}}`,
      usage: 134,
      performance: {
        openRate: 42.1,
        replyRate: 9.8,
        conversionRate: 6.2
      },
      lastUsed: '2025-01-05'
    }
  ];

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'email':
        return 'Mail';
      case 'sms':
        return 'MessageSquare';
      case 'social':
        return 'Share2';
      default:
        return 'FileText';
    }
  };

  const getChannelColor = (channel) => {
    switch (channel) {
      case 'email':
        return 'text-primary';
      case 'sms':
        return 'text-secondary';
      case 'social':
        return 'text-warning';
      default:
        return 'text-muted-foreground';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'cold-outreach':
        return 'bg-primary/10 text-primary';
      case 'follow-up':
        return 'bg-secondary/10 text-secondary';
      case 'demo-invitation':
        return 'bg-accent/10 text-accent';
      case 'thank-you':
        return 'bg-success/10 text-success';
      case 'nurturing':
        return 'bg-warning/10 text-warning';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const filteredTemplates = templates?.filter(template => {
    const matchesCategory = !selectedCategory || template?.category === selectedCategory;
    const matchesSearch = template?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         template?.content?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header and Filters */}
      <div className="bg-card border border-border rounded-lg p-4 shadow-elevation-1">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <h3 className="text-lg font-semibold text-foreground">Template Library</h3>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <Input
              type="search"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="sm:w-64"
            />
            <Select
              placeholder="Category"
              options={categories}
              value={selectedCategory}
              onChange={setSelectedCategory}
              className="sm:w-48"
            />
            <Button variant="default" iconName="Plus" iconPosition="left">
              New Template
            </Button>
          </div>
        </div>
      </div>
      {/* Templates Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredTemplates?.map((template) => (
          <div
            key={template?.id}
            className="bg-card border border-border rounded-lg shadow-elevation-1 hover-elevation transition-all duration-200"
          >
            <div className="p-4">
              {/* Template Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Icon
                    name={getChannelIcon(template?.channel)}
                    size={16}
                    className={getChannelColor(template?.channel)}
                  />
                  <h4 className="font-medium text-foreground text-sm">{template?.name}</h4>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(template?.category)}`}>
                  {template?.category?.replace('-', ' ')}
                </span>
              </div>

              {/* Subject Line */}
              {template?.subject && (
                <div className="mb-3">
                  <p className="text-xs text-muted-foreground mb-1">Subject:</p>
                  <p className="text-sm font-medium text-foreground line-clamp-1">
                    {template?.subject}
                  </p>
                </div>
              )}

              {/* Content Preview */}
              <div className="mb-4">
                <p className="text-xs text-muted-foreground mb-1">Content:</p>
                <p className="text-sm text-foreground line-clamp-4 whitespace-pre-line">
                  {template?.content}
                </p>
              </div>

              {/* Performance Metrics */}
              <div className="mb-4 p-3 bg-muted rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {template?.channel === 'email' && (
                    <>
                      <div>
                        <span className="text-muted-foreground">Open Rate:</span>
                        <span className="font-medium text-foreground ml-1">
                          {template?.performance?.openRate}%
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Reply Rate:</span>
                        <span className="font-medium text-foreground ml-1">
                          {template?.performance?.replyRate}%
                        </span>
                      </div>
                    </>
                  )}
                  {template?.channel === 'sms' && (
                    <>
                      <div>
                        <span className="text-muted-foreground">Delivery:</span>
                        <span className="font-medium text-foreground ml-1">
                          {template?.performance?.deliveryRate}%
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Reply Rate:</span>
                        <span className="font-medium text-foreground ml-1">
                          {template?.performance?.replyRate}%
                        </span>
                      </div>
                    </>
                  )}
                  {template?.channel === 'social' && (
                    <>
                      <div>
                        <span className="text-muted-foreground">Acceptance:</span>
                        <span className="font-medium text-foreground ml-1">
                          {template?.performance?.acceptanceRate}%
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Response:</span>
                        <span className="font-medium text-foreground ml-1">
                          {template?.performance?.responseRate}%
                        </span>
                      </div>
                    </>
                  )}
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Conversion:</span>
                    <span className="font-medium text-success ml-1">
                      {template?.performance?.conversionRate}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Usage Stats */}
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                <span>Used {template?.usage} times</span>
                <span>Last used: {template?.lastUsed}</span>
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <Button
                  variant="default"
                  size="sm"
                  iconName="Eye"
                  iconPosition="left"
                  onClick={() => setSelectedTemplate(template)}
                  className="flex-1"
                >
                  Preview
                </Button>
                <Button variant="outline" size="sm" iconName="Copy" iconSize={14}>
                  Use
                </Button>
                <Button variant="outline" size="sm" iconName="Edit" iconSize={14}>
                  Edit
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Template Preview Modal */}
      {selectedTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg shadow-elevation-3 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">
                  {selectedTemplate?.name}
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={() => setSelectedTemplate(null)}
                />
              </div>
              
              {selectedTemplate?.subject && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Subject Line:
                  </label>
                  <p className="p-3 bg-muted rounded-lg text-sm text-foreground">
                    {selectedTemplate?.subject}
                  </p>
                </div>
              )}
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Content:
                </label>
                <div className="p-4 bg-muted rounded-lg">
                  <pre className="text-sm text-foreground whitespace-pre-wrap font-sans">
                    {selectedTemplate?.content}
                  </pre>
                </div>
              </div>
              
              <div className="flex space-x-3">
                <Button variant="default" iconName="Copy" iconPosition="left" className="flex-1">
                  Use This Template
                </Button>
                <Button variant="outline" iconName="Edit" iconPosition="left">
                  Edit Template
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Empty State */}
      {filteredTemplates?.length === 0 && (
        <div className="bg-card border border-border rounded-lg p-8 text-center shadow-elevation-1">
          <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h4 className="text-lg font-medium text-foreground mb-2">No templates found</h4>
          <p className="text-muted-foreground mb-4">
            Try adjusting your search criteria or create a new template
          </p>
          <Button variant="default" iconName="Plus" iconPosition="left">
            Create New Template
          </Button>
        </div>
      )}
    </div>
  );
};

export default TemplateLibrary;